import {
  createScriptElement,
  type ScriptElement,
  type ScriptElementType,
} from '@/features/studio/script/script-elements'
import type { ScriptConvention } from '@/features/studio/script/useScriptFormatPrefs'
import { prepareFountainBodyForParse } from '@/features/studio/script/fountain-preparse'

const SCENE_HEADING_HOLLYWOOD =
  /^(?:\.|INT\.|EXT\.|INT\.\/EXT\.|I\/E\.|E\/I\.|INT\/EXT\.)/i
const SCENE_HEADING_CN_NUMBERED = /^\d+\.\s*(?:内景|外景|场景)/
const SCENE_HEADING_CN = /^(?:内景|外景|场景)/
const TRANSITION_HOLLYWOOD = /^(?:CUT TO:|SMASH CUT TO:|FADE OUT\.|FADE IN:|FADE TO:|DISSOLVE TO:)/i
const TRANSITION_CN = /^切至[：:]?\s*$/
const SHOT_CN = /^镜头[·：]/
const SHOT_HOLLYWOOD = /^(?:ANGLE|CLOSE ON|POV|INSERT|WIDE ON|MONTAGE)/i
const NOTE_PATTERN = /^\[\[([\s\S]*)\]\]$/
const PAREN_CN = /^[\(（]([^)）]*)[\)）]$/
const PAREN_HOLLYWOOD = /^\(([^)]*)\)$/

function isSceneHeading(line: string, convention: ScriptConvention): boolean {
  if (SCENE_HEADING_HOLLYWOOD.test(line)) return true
  if (convention === 'cn') {
    return SCENE_HEADING_CN_NUMBERED.test(line) || SCENE_HEADING_CN.test(line)
  }
  return false
}

function isTransition(line: string, convention: ScriptConvention): boolean {
  if (TRANSITION_HOLLYWOOD.test(line)) return true
  if (convention === 'cn' && TRANSITION_CN.test(line)) return true
  return /TO:\s*$/i.test(line)
}

function isShot(line: string, convention: ScriptConvention): boolean {
  if (SHOT_CN.test(line)) return true
  if (convention === 'hollywood' && SHOT_HOLLYWOOD.test(line)) return true
  return false
}

function isNote(line: string): boolean {
  return NOTE_PATTERN.test(line) || line.startsWith('※')
}

function parseNoteContent(line: string): string {
  const m = line.match(NOTE_PATTERN)
  if (m) return m[1].trim()
  return line.replace(/^※\s?/, '').trim()
}

function isHollywoodCharacter(line: string): boolean {
  if (line.length < 1 || line.length > 64) return false
  if (isSceneHeading(line, 'hollywood') || isTransition(line, 'hollywood')) return false
  const stripped = line.replace(/\s*\([^)]*\)\s*$/, '')
  if (!stripped) return false
  return stripped === stripped.toUpperCase() && /[A-Z]/.test(stripped)
}

function isCnCharacter(line: string, nextLine: string | null): boolean {
  if (line.length < 1 || line.length > 24) return false
  if (isSceneHeading(line, 'cn') || isTransition(line, 'cn') || isShot(line, 'cn') || isNote(line)) {
    return false
  }
  if (/[。！？；：]/.test(line)) return false
  if (/^(内景|外景|场景|切至|镜头)/.test(line)) return false
  if (!nextLine) return false
  if (PAREN_CN.test(nextLine)) return true
  if (isSceneHeading(nextLine, 'cn') || isTransition(nextLine, 'cn') || isShot(nextLine, 'cn')) {
    return false
  }
  return true
}

function isStructuralLine(line: string, convention: ScriptConvention): boolean {
  return (
    isSceneHeading(line, convention) ||
    isTransition(line, convention) ||
    isShot(line, convention) ||
    isNote(line)
  )
}

function peekLine(lines: string[], from: number): string | null {
  for (let j = from; j < lines.length; j++) {
    const t = lines[j].trim()
    if (t) return t
  }
  return null
}

export function parseFountainToElements(
  body: string,
  convention: ScriptConvention = 'cn',
): ScriptElement[] {
  const normalized = prepareFountainBodyForParse(body, convention).replace(/\r\n/g, '\n').trim()
  if (!normalized) {
    return [createScriptElement('action', '', 1)]
  }

  const lines = normalized.split('\n')
  const elements: ScriptElement[] = []
  let sceneNo = 0
  let i = 0

  const push = (type: ScriptElementType, content: string) => {
    if (type === 'scene_heading') {
      sceneNo += 1
      const m = content.match(/^(\d+)\./)
      if (m) sceneNo = Number(m[1])
      let heading = content
      if (convention === 'cn') {
        const stripped = content.match(/^\d+\.\s*(.+)$/)
        if (stripped) heading = stripped[1]!.trim()
      }
      elements.push(createScriptElement(type, heading, sceneNo))
      return
    }
    elements.push(createScriptElement(type, content, sceneNo || 1))
  }

  while (i < lines.length) {
    const trimmed = lines[i].trim()
    if (!trimmed) {
      i += 1
      continue
    }

    if (isSceneHeading(trimmed, convention)) {
      push('scene_heading', trimmed)
      i += 1
      continue
    }

    if (isTransition(trimmed, convention)) {
      push('transition', trimmed)
      i += 1
      continue
    }

    if (isShot(trimmed, convention)) {
      push('shot', trimmed)
      i += 1
      continue
    }

    if (isNote(trimmed)) {
      push('note', parseNoteContent(trimmed))
      i += 1
      continue
    }

    const next = peekLine(lines, i + 1)
    const isCharacter =
      convention === 'hollywood'
        ? isHollywoodCharacter(trimmed)
        : isCnCharacter(trimmed, next)

    if (isCharacter) {
      push('character', trimmed)
      i += 1
      while (i < lines.length && !lines[i].trim()) i += 1
      const maybeParen = i < lines.length ? lines[i].trim() : ''
      if (maybeParen && (PAREN_CN.test(maybeParen) || PAREN_HOLLYWOOD.test(maybeParen))) {
        const inner =
          maybeParen.match(PAREN_CN)?.[1] ?? maybeParen.match(PAREN_HOLLYWOOD)?.[1] ?? maybeParen
        push('parenthetical', inner.trim())
        i += 1
        while (i < lines.length && !lines[i].trim()) i += 1
      }
      const dialogueLines: string[] = []
      while (i < lines.length) {
        const dl = lines[i].trim()
        if (!dl) break
        if (isStructuralLine(dl, convention)) break
        if (convention === 'hollywood' && isHollywoodCharacter(dl)) break
        if (convention === 'cn' && isCnCharacter(dl, peekLine(lines, i + 1))) break
        dialogueLines.push(dl)
        i += 1
      }
      if (dialogueLines.length > 0) {
        push('dialogue', dialogueLines.join('\n'))
      }
      continue
    }

    while (i < lines.length) {
      const al = lines[i].trim()
      if (!al) break
      if (isStructuralLine(al, convention)) break
      const nxt = peekLine(lines, i + 1)
      if (convention === 'hollywood' && isHollywoodCharacter(al)) break
      if (convention === 'cn' && isCnCharacter(al, nxt)) break
      push('action', al)
      i += 1
    }
  }

  if (elements.length === 0) {
    return [createScriptElement('action', normalized, 1)]
  }

  return elements
}

export function findSceneHeadingElementId(
  elements: ScriptElement[],
  sceneNo: number,
): string | null {
  const hit = elements.find((e) => e.type === 'scene_heading' && e.sceneNo === sceneNo)
  return hit?.id ?? null
}
